import { isEn, t } from "../i18n";
import { drawMed, MED_IMAGES, placement } from "../art/med";
import { drawMonitor, type MonitorData } from "../art/monitors";
import { $, h } from "../lib/dom";
import { resolve } from "../lib/util";
import { makeBtn, updateControls } from "./dialog";
import { getImagery, setImagery } from "./progress";
import { rt, S } from "./state";
import { renderHUD } from "./time";
import type { Choice, Hotspot, PhotoView, Zoom, ZoomCfg } from "./types";

/** Close-up canvas resolution. */
export const ZW = 192;
export const ZH = 108;

const zc = $<HTMLCanvasElement>("#zc");
const zctxOrNull = zc.getContext("2d");
if (!zctxOrNull) throw new Error("2d context unavailable");
export const zctx = zctxOrNull;
zctx.imageSmoothingEnabled = false;

let Z: Zoom | null = null;

export const isZoomOpen = (): boolean => Z !== null;
/** The open close-up's config (levels may tweak hint/bar on it). */
export const currentZoom = (): Zoom | null => Z;
export const zoomDialog = (): string | undefined => Z?.dlg;

/** The real image to show right now, if this close-up has one and the player prefers photos. */
function activePhoto(): PhotoView | null {
  if (!Z?.photo || getImagery() !== "photo") return null;
  return resolve(Z.photo);
}

/** Header key last rendered, so the per-frame check only touches the DOM on change. */
let headKey = "";

function renderZHead(): void {
  if (!Z) return;
  const ph = activePhoto();
  // Credits are generated with a full-width comma (scripts/med-images); Latin text wants ", ".
  const raw = ph ? MED_IMAGES[ph.id].credit : "";
  const credit = ph ? t("zoom.credit", { credit: isEn ? raw.replace(/，/g, ", ") : raw }) : ""; // i18n-ok
  $("#zoom .zh b").textContent = Z.title;
  $("#zoom .zh span").textContent = [Z.sub ?? "", credit].filter(Boolean).join(t("log.sep"));
  const btn = $<HTMLButtonElement>("#zphoto");
  btn.hidden = !(Z.photoToggle ?? (Z.photo ? resolve(Z.photo) : null));
  btn.textContent = getImagery() === "photo" ? t("zoom.drawn") : t("zoom.photo");
  headKey = `${ph?.id ?? ""}|${getImagery()}|${String(btn.hidden)}`;
}

export function openZoom(cfg: ZoomCfg): void {
  Z = { hot: [], bar: [], barLabel: "", ...cfg, hover: null };
  const z = $("#zoom");
  renderZHead();
  z.classList.add("show");
  $("#zlabel").style.display = "none";
  renderZBar();
  requestAnimationFrame(fitZoom);
  cfg.onOpen?.();
}

/** `byUser` = Esc / close button, which also fires `onBack`. */
export function closeZoom(byUser = false): void {
  if (!Z) return;
  const z = Z;
  Z = null;
  $("#zoom").classList.remove("show");
  $("#zlabel").style.display = "none";
  if (S.lap) S.lap.pan = null;
  z.onClose?.();
  if (byUser && S.live) z.onBack?.();
}

export function setZBar(items: Choice[], label = ""): void {
  if (!Z) return;
  Z.bar = items;
  Z.barLabel = label;
  renderZBar();
}

export function renderZBar(): void {
  const b = $("#zbar");
  b.replaceChildren();
  if (!Z || !S.live) return;
  if (Z.barLabel) b.appendChild(h("span", { className: "lbl", text: Z.barLabel }));
  for (const it of resolve(Z.bar ?? []))
    b.appendChild(
      makeBtn(it, () => {
        renderZBar();
        updateControls();
        renderHUD();
      }),
    );
  if (Z.hint) b.appendChild(h("span", { className: "hint", text: Z.hint }));
}

function fitZoom(): void {
  if (!Z) return;
  const w = $("#zoom .zwrap");
  const s = Math.max(1, Math.min(w.clientWidth / ZW, w.clientHeight / ZH));
  zc.style.width = `${Math.floor(ZW * s)}px`;
  zc.style.height = `${Math.floor(ZH * s)}px`;
}

function zPos(e: PointerEvent): [number, number, DOMRect] {
  const r = zc.getBoundingClientRect();
  return [((e.clientX - r.left) / r.width) * ZW, ((e.clientY - r.top) / r.height) * ZH, r];
}

/** Hotspots in close-up coordinates (photo hotspots are shifted from image coordinates). */
function hotspots(): Hotspot[] {
  if (!Z) return [];
  const ph = activePhoto();
  if (!ph) return resolve(Z.hot ?? []);
  const p = placement(ph.id);
  return resolve(ph.hot ?? []).map((h) => ({ ...h, x: h.x + p.x, y: h.y + p.y }));
}

function zHot(x: number, y: number): Hotspot | null {
  return hotspots().find((s) => x >= s.x && y >= s.y && x < s.x + s.w && y < s.y + s.h) ?? null;
}

/** Draw the open close-up (called from the frame loop). */
export function drawZoom(now: number): void {
  if (!Z) return;
  const ph = activePhoto();
  const key = `${ph?.id ?? ""}|${getImagery()}`;
  if (!headKey.startsWith(key + "|")) renderZHead();
  if (ph) {
    const p = drawMed(zctx, ph.id);
    ph.overlay?.(zctx, p, now);
  } else if (Z.draw) Z.draw(zctx, now);
  else return;
  const hv = Z.hover;
  if (hv && hv.w < 190) {
    zctx.strokeStyle = "#ffd447";
    zctx.lineWidth = 1;
    zctx.setLineDash([2, 2]);
    zctx.strokeRect(hv.x + 0.5, hv.y + 0.5, hv.w - 1, hv.h - 1);
    zctx.setLineDash([]);
  }
}

/** Esc closes; otherwise the zoom's own key handler. Returns true if consumed. */
export function zoomKey(e: KeyboardEvent): boolean {
  if (!Z) return false;
  if (e.key === "Escape") {
    closeZoom(true);
    return true;
  }
  return !!Z.onKey?.(e);
}

export function initZoom(): void {
  new ResizeObserver(fitZoom).observe($("#zoom .zwrap"));
  $("#zclose").onclick = () => closeZoom(true);
  $("#zphoto").onclick = () => {
    setImagery(getImagery() === "photo" ? "drawn" : "photo");
    if (Z) Z.hover = null;
    $("#zlabel").style.display = "none";
    renderZHead();
  };

  interface Drag {
    x: number;
    y: number;
    travel: number;
    moved: boolean;
  }
  let drag: Drag | null = null;

  zc.addEventListener("pointermove", (e) => {
    if (!Z) return;
    const [x, y, r] = zPos(e);
    if (drag && Z.onDrag) {
      const dx = x - drag.x;
      const dy = y - drag.y;
      const travel = drag.travel + Math.abs(dx) + Math.abs(dy);
      if (travel > 3) {
        Z.onDrag(dx, dy);
        drag = { x, y, travel, moved: true };
      }
      return;
    }
    const hot = zHot(x, y);
    Z.hover = hot;
    const lab = $("#zlabel");
    if (hot) {
      lab.textContent = hot.label;
      lab.style.display = "block";
      lab.style.left = `${e.clientX - r.left + zc.offsetLeft}px`;
      lab.style.top = `${e.clientY - r.top + zc.offsetTop}px`;
    } else lab.style.display = "none";
  });
  zc.addEventListener("pointerleave", () => {
    if (Z) Z.hover = null;
    $("#zlabel").style.display = "none";
  });
  zc.addEventListener("pointerdown", (e) => {
    if (!Z?.onDrag) return;
    const [x, y] = zPos(e);
    drag = { x, y, travel: 0, moved: false };
    zc.setPointerCapture(e.pointerId);
  });
  zc.addEventListener("pointerup", (e) => {
    if (!Z) return;
    const wasDrag = drag?.moved ?? false;
    drag = null;
    if (wasDrag || S.busy || S.ended || S.cine) return;
    const [x, y] = zPos(e);
    const hot = zHot(x, y);
    if (hot) {
      rt.fastForward = false;
      hot.fn();
      renderZBar();
      updateControls();
      renderHUD();
    }
  });
  zc.addEventListener(
    "wheel",
    (e) => {
      if (Z?.onWheel) {
        e.preventDefault();
        Z.onWheel(e.deltaY);
      }
    },
    { passive: false },
  );
}

/** Close-up of a vitals monitor fed by a live data getter. */
export function openMonitor(title: string, data: () => MonitorData, bar: Choice[] = [], onBack?: () => void): void {
  openZoom({
    title,
    sub: t("zoom.monitor"),
    draw: (c, now) => drawMonitor(c, now, data()),
    bar,
    ...(onBack ? { onBack } : {}),
  });
}
